/**
 * PATH: packages/email-hub/src/feature/components/WhyPanel.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-WHYPANEL-001
 * PURPOSE: Collapsible "Why this result?" panel — shows persisted provenance
 *          for an email: decision journal citation (RT-03), classification
 *          sidecar, entity links, and factual run metrics (attachments /
 *          latency) without inventing "by AI" placement copy.
 * WHY:     INV-03 / RT-03 — Why must cite EmailDecisionJournal (or honest
 *          legacy_evidence_unavailable), never synthetic filing prose.
 * RELATED:
 *   - hooks/useEmailExplain.ts: the data source.
 *   - AIEmailPanel.tsx: renders this beneath the analysis.
 */

import { useEffect, useState } from 'react'
import { ChevronDown, ChevronRight, Loader2, ShieldCheck } from 'lucide-react'
import { cn } from '../../_internal/lib/utils'
import {
  useEmailExplain,
  type EmailExplanation,
  type ExplainDecision,
  type ExplainRun,
} from '../hooks/useEmailExplain'

const STATUS_STYLES: Record<string, string> = {
  confirmed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
}

/**
 * Pure: factual run metrics only (attachments / latency).
 * RT-03: do NOT invent "by AI" / "by rules" attribution from payload —
 * decision attribution comes from the journal block.
 * Exported for unit testing.
 */
export function summariseRuns(runs: ExplainRun[]): string {
  const succeeded = runs.find((r) => r.status === 'succeeded')
  const run = succeeded ?? runs[0]
  if (!run) return ''
  const parts: string[] = []
  const read = run.payload?.read_attachments
  if (typeof read === 'number' && read > 0) {
    parts.push(`read ${read} attachment${read > 1 ? 's' : ''}`)
  }
  if (typeof run.latency_ms === 'number') parts.push(`${run.latency_ms}ms`)
  return parts.join(' · ')
}

/**
 * Pure: citation line from persisted decision evidence.
 * Non-empty reason requires journal_id (RT-03 API/UI citation contract).
 * Exported for unit testing.
 */
export function summariseDecision(decision: ExplainDecision | null | undefined): string {
  if (!decision) return ''
  if (decision.legacy_evidence_unavailable || decision.journal_id == null) {
    return 'No persisted decision journal for this email'
  }
  const reason = (decision.reason || '').trim()
  if (reason) return reason
  const tier = (decision.action_tier || '').trim()
  const route =
    decision.selected_route && typeof decision.selected_route['route'] === 'string'
      ? String(decision.selected_route['route'])
      : ''
  if (tier && route) return `${tier} → ${route}`
  if (tier) return tier
  if (route) return route
  return `Journal #${decision.journal_id}`
}

function formatConfidence(value: number | null): string {
  if (value == null) return '—'
  const pct = value <= 1 ? Math.round(value * 100) : Math.round(value)
  return `${pct}%`
}

function Body({ data }: { data: EmailExplanation }) {
  const runSummary = summariseRuns(data.runs)
  const decisionSummary = summariseDecision(data.decision)
  const { classification: c, entity_links: links, decision } = data
  const evidenceRead = decision?.evidence_read?.length
    ? decision.evidence_read.map(String).join(', ')
    : ''
  const evidenceMissing = decision?.evidence_missing?.length
    ? decision.evidence_missing.map(String).join(', ')
    : ''

  return (
    <div className="px-3 pb-3 space-y-3 text-sm">
      {/* RT-03 — persisted decision journal first */}
      <div className="space-y-1">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Decision evidence
        </p>
        <p className="text-xs text-foreground">{decisionSummary || '—'}</p>
        {decision && !decision.legacy_evidence_unavailable && decision.journal_id != null && (
          <div className="flex items-center gap-2 flex-wrap text-[10px] text-muted-foreground">
            <span>journal #{decision.journal_id}</span>
            {decision.action_tier && <span>{decision.action_tier}</span>}
            {typeof decision.confidence === 'number' && (
              <span>conf {formatConfidence(decision.confidence)}</span>
            )}
          </div>
        )}
        {evidenceRead && (
          <p className="text-[10px] text-muted-foreground">Read: {evidenceRead}</p>
        )}
        {evidenceMissing && (
          <p className="text-[10px] text-muted-foreground">Missing: {evidenceMissing}</p>
        )}
        {decision?.blocked_reason ? (
          <p className="text-[10px] text-amber-700 dark:text-amber-300">
            Blocked: {decision.blocked_reason}
          </p>
        ) : null}
      </div>

      {/* Classification provenance (sidecar — not synthetic Why copy) */}
      <div className="space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-muted-foreground text-xs">Classified</span>
          {c.source && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-muted text-foreground/80">
              {c.source}
            </span>
          )}
          <span className="text-xs">conf {formatConfidence(c.confidence)}</span>
          {runSummary && <span className="text-[10px] text-muted-foreground">({runSummary})</span>}
        </div>
        {c.override && (
          <div className="flex items-center gap-1.5 text-[11px] text-amber-700 dark:text-amber-300">
            <ShieldCheck className="h-3 w-3" />
            Operator override: {c.override.category}
            {c.override.locked && ' (locked)'}
          </div>
        )}
      </div>

      {/* Resolved entity links */}
      {links.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
            Resolved links
          </p>
          <div className="space-y-1">
            {links.map((l, i) => (
              <div key={i} className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-medium uppercase text-muted-foreground">{l.entity_type}</span>
                <span className="text-foreground font-medium">{l.label || l.entity_id}</span>
                <span className={cn('px-1.5 py-0.5 text-[9px] font-semibold uppercase rounded-full', STATUS_STYLES[l.status] || STATUS_STYLES.pending)}>
                  {l.status}
                </span>
                {l.reasoning && <span className="text-[10px] text-muted-foreground truncate">{l.reasoning}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {links.length === 0 && (
        <p className="text-[11px] text-muted-foreground">No entities resolved for this email.</p>
      )}
    </div>
  )
}

export default function WhyPanel({ emailId }: { emailId: string }) {
  const { data, loading, error, load } = useEmailExplain()
  // Operator 2026-06-13 ("have all sections expanded, you don't want to
  // click when scrolling down"). Start OPEN and lazy-load the provenance
  // on mount (one cheap GET, no LLM) so "Why this result?" is visible
  // without a click. The header toggle still collapses it on demand.
  const [open, setOpen] = useState(true)

  useEffect(() => {
    // Re-fetch when the reader switches to a different email while the panel
    // is open. BUG-2026-06-23: the old `!data` gate meant that once ANY
    // email's provenance had loaded, switching emails never reloaded — the
    // panel froze on the first email's data. Reload whenever the currently
    // loaded explanation isn't for THIS email (covers first open + switch).
    // `load`/`data`/`loading` are intentionally omitted: including them
    // re-fires on every fetch-state transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (open && data?.email_id !== emailId && !loading) load(emailId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailId])

  const toggle = () => {
    const next = !open
    setOpen(next)
    if (next && !data && !loading) load(emailId)
  }

  return (
    <div className="border-t border-violet-200/40 dark:border-violet-800/30 pt-2">
      <button
        onClick={toggle}
        className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        Why this result?
        {loading && <Loader2 className="h-3 w-3 animate-spin" />}
      </button>
      {open && error && <p className="px-3 pt-2 text-xs text-destructive">{error}</p>}
      {open && data && <Body data={data} />}
    </div>
  )
}
